// The one sentence at the top of a Fit or Engagement hover. A plain template over the real numbers
// (no model call): the one or two categories that add the most points, then any competitor cap and
// any category with no data. Plain .ts so node --test can load it.

import type { BreakdownLine, CompetitorFlagLine } from './scoreBreakdown.ts';

// What a strong category says, as a clause that stands alone.
const STRENGTH_PHRASES: Record<string, string> = {
  voice_is_product: 'voice is their product',
  voice_product: 'voice is their product',
  voice_use_case: 'they have a clear voice use case',
  lookalikes: 'they look like our best customers',
  ai_team: 'they have a strong AI team',
  tech_native: 'they are tech and AI-native',
  tech_ai_native: 'they are tech and AI-native',
  hiring: 'they are hiring for voice or AI',
  playground: 'they use the self-serve product',
  web_intent: 'they have been visiting our site',
  news: 'there is recent news about them',
  voice_news: 'there is recent news about them',
};
const MAX_TOP_CATEGORIES = 2;

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function strengthPhrase(line: BreakdownLine): string {
  return STRENGTH_PHRASES[line.category] ?? `${line.label.toLowerCase()} scores well`;
}

function joinWithAnd(items: string[]): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

// A category the engine had no data for, whether it left it out (weight 0) or fell back to a default.
function missingClause(missing: BreakdownLine[]): string {
  const names = joinWithAnd(missing.map(line => line.label));
  const allLeftOut = missing.every(line => line.weight === 0);
  const single = missing.length === 1;
  const outcome = allLeftOut ? (single ? 'it is left out' : 'they are left out') : (single ? 'a default is used' : 'defaults are used');
  return `${names} ${single ? 'has' : 'have'} no data, so ${outcome}`;
}

export function scoreSentence(kind: 'fit' | 'engagement', lines: BreakdownLine[], competitor: CompetitorFlagLine | null): string {
  // The categories that add the most points, strongest first; a category with no data is not strong.
  const strongest = lines
    .filter(line => line.state === 'scored' && (line.points ?? 0) > 0)
    .sort((first, second) => (second.points ?? 0) - (first.points ?? 0))
    .slice(0, MAX_TOP_CATEGORIES);
  const missing = lines.filter(line => line.state === 'default');

  // The opening clause: what drives the score, or a plain statement that nothing does yet.
  const drivers = strongest.map(strengthPhrase);
  const opening = drivers.length
    ? capitalize(joinWithAnd(drivers))
    : `Nothing adds to ${kind === 'fit' ? 'Fit' : 'Engagement'} yet`;

  // After the semicolon: the competitor cap, then the missing data.
  const clauses: string[] = [];
  if (competitor && !competitor.lifted) {
    clauses.push(competitor.points != null
      ? `flagged as a competitor, so Fit is capped at ${competitor.calc.replace('capped at ', '')}`
      : 'flagged as a competitor, so Fit is held low');
  }
  if (missing.length) clauses.push(missingClause(missing));
  return `${opening}${clauses.length ? '; ' + clauses.join('; ') : ''}.`;
}
